import api from './api';
import { getToken } from '../utils/storage';
import cloudinaryService from './cloudinaryService';
import { 
  ApiResponse,
  PaginationParams 
} from '../types/api';
import { Payment } from '../types/profile';

// Import new types
import {
  SubscriptionPlan,
  CustomerTokenBalance,
  TokenTransaction,
  Subscription,
  EffectiveStatusResponse,
  GetPlansParams,
  CustomerType
} from '../types/subscription';

// ============================================
// 📱 TYPES MOBILE MONEY - Conformes à la documentation backend
// ============================================

/**
 * Codes télécom supportés par le backend
 * - AM: Airtel Money (*150#)
 * - OM: Orange Money (#150#)
 * - MP: M-Pesa (*100#)
 * - AF: Africell (*144#)
 */
export type TelecomCode = 'AM' | 'OM' | 'MP' | 'AF';

/**
 * Channel de paiement Mobile Money
 * - merchant: Push (le système envoie la demande)
 * - client: Pull (l'utilisateur initie via USSD)
 */
export type PaymentChannel = 'merchant' | 'client';

/**
 * Période de facturation
 */
export type BillingPeriod = 'monthly' | 'annual';

/**
 * Requête d'achat d'abonnement via Mobile Money
 * POST /subscriptions/purchase
 */
export interface PurchaseRequest {
  planId: string;              // UUID du plan (ex: "a1b2c3d4-0002-4000-8000-000000000002")
  clientPhone: string;         // Format: 243XXXXXXXXX (12 chiffres, préfixe RDC)
  telecom: TelecomCode;        // Code opérateur: AM, OM, MP, AF
  channel?: PaymentChannel;    // 'merchant' (défaut) ou 'client'
  clientReference?: string;    // Référence client optionnelle
  billingPeriod?: BillingPeriod;  // 🆕 Période: 'monthly' ou 'annual'
  promoCode?: string;          // 🆕 Code promo optionnel
  /**
   * Nombre d'unités souscrites pour les plans `per_unit` (coopératives :
   * nombre de coopérants). Ignoré côté backend pour les plans flat.
   * Si non fourni, `purchasePlan` lit `sessionStorage.cooperative_member_count`.
   */
  quantity?: number;
}

/**
 * Requête d'upgrade d'abonnement
 * POST /subscriptions/upgrade
 */
export interface UpgradeSubscriptionRequest {
  newPlanId: string;           // UUID du nouveau plan
  clientPhone: string;         // Format: 243XXXXXXXXX
  telecom: TelecomCode;        // Code opérateur
  billingPeriod?: BillingPeriod;  // Période de facturation
  promoCode?: string;          // Code promo optionnel
  applyImmediately?: boolean;  // Appliquer immédiatement (défaut: true)
}

/**
 * Réponse de la prévisualisation de prix
 * GET /subscriptions/price-preview
 */
export interface PricePreviewResponse {
  success: boolean;
  data: {
    baseAmount: number;         // Prix de base
    discountAmount: number;     // Réduction période (annuelle)
    promoDiscountAmount: number; // Réduction code promo
    proratedCredit: number;     // Crédit prorata (upgrade)
    finalAmount: number;        // Montant final à payer
    currency: string;           // Devise (USD, CDF)
    promoApplied: boolean;      // Code promo valide
    planDetails: {
      id: string;
      name: string;
      monthlyPrice: number;
      annualPrice: number;
    };
  };
}

/**
 * Réponse succès d'un achat Mobile Money (status: pending)
 * Le paiement est initié, en attente de confirmation utilisateur
 */
export interface PurchaseResponse {
  success: boolean;
  data: {
    transactionId: string;
    status: 'pending' | 'completed' | 'failed';
    httpStatus: number;
    message: string;
    plan: {
      id: string;
      name: string;
      tokensIncluded: number;
    };
    instructions: string;
  };
}

/**
 * Réponse du statut d'un paiement Mobile Money
 * GET /subscriptions/payment-status/{transactionId}
 */
export interface PaymentStatusResponse {
  transactionId: string;
  status: 'pending' | 'completed' | 'failed' | 'expired';
  subscription?: Subscription;
  failureReason?: string;
  message?: string;
  provider?: string;
  phoneNumber?: string;
  amount?: number;
  currency?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Réponse pour un paiement Mobile Money en attente
 * Utilisé par le hook useMobileMoneyPayment pour le polling
 */
export interface MobileMoneyPendingResponse {
  id: string;
  planId: string;
  status: 'pending';
  transactionId: string;
  paymentStatus: 'pending';
  message: string;
  checkStatusUrl: string;
  expiresAt: string;
  provider?: string;
  phoneNumber?: string;
  instructions?: string;
  telecom?: TelecomCode;
}

/**
 * Élément de l'historique des paiements
 * GET /subscriptions/current/payment-history
 */
export interface PaymentHistoryItem {
  transactionId: string;
  providerTransactionId?: string;
  
  planId: string;
  planName: string;
  planType: 'monthly' | 'annual';
  subscriptionId: string;
  
  amount: string;
  currency: string;
  
  status: 'pending' | 'success' | 'failed';
  
  telecom: TelecomCode;
  clientPhone: string;
  provider: string;
  
  createdAt: string;
  completedAt?: string;
  
  billingPeriod?: {
    start: string;
    end: string;
  };
  
  tokensIncluded: number;
  isRenewal: boolean;
}

/**
 * Mappage des codes télécom vers les noms d'opérateurs
 */
export const TELECOM_PROVIDERS: Record<TelecomCode, { name: string; ussd: string }> = {
  AM: { name: 'Airtel Money', ussd: '*150#' },
  OM: { name: 'Orange Money', ussd: '#150#' },
  MP: { name: 'M-Pesa', ussd: '*100#' },
  AF: { name: 'Africell', ussd: '*144#' }
};

/**
 * Convertit un nom d'opérateur en code télécom
 */
export function getProviderTelecomCode(providerName: string): TelecomCode {
  const mapping: Record<string, TelecomCode> = {
    'airtel': 'AM',
    'airtel_money': 'AM',
    'airtel money': 'AM',
    'orange': 'OM',
    'orange_money': 'OM',
    'orange money': 'OM',
    'mpesa': 'MP',
    'm-pesa': 'MP',
    'vodacom': 'MP',
    'africell': 'AF',
    'afrimoney': 'AF'
  };
  return mapping[providerName.toLowerCase()] || 'AM';
}

/**
 * Le backend renvoie soit { success, data }, soit directement la ressource :
 * on prend `data` s'il est présent, sinon la réponse elle-même.
 */
function unwrapData<T>(response: ApiResponse<T> | T): T {
  const wrapped = response as Partial<ApiResponse<T>>;
  return wrapped.data || (response as T);
}

/**
 * Formate un numéro de téléphone au format backend (243XXXXXXXXX)
 * @param countryCode Code pays (+243, 243, etc.)
 * @param phoneNumber Numéro local (avec ou sans le code pays)
 * @returns Numéro au format 243XXXXXXXXX
 */
export function formatPhoneNumber(countryCode: string, phoneNumber: string): string {
  // Nettoyer le code pays (retirer + et espaces)
  const cleanCode = countryCode.replace(/[+\s]/g, '');
  
  // Nettoyer le numéro (retirer espaces, tirets, etc.)
  let cleanNumber = phoneNumber.replace(/[\s\-()]/g, '');
  
  // Si le numéro commence déjà par le code pays, ne pas le doubler
  if (cleanNumber.startsWith(cleanCode)) {
    return cleanNumber;
  }
  
  // Si le numéro commence par 0, le retirer
  if (cleanNumber.startsWith('0')) {
    cleanNumber = cleanNumber.substring(1);
  }
  
  return `${cleanCode}${cleanNumber}`;
}

/**
 * Service pour la gestion des abonnements, conforme à la documentation API
 */
class SubscriptionApiService {
  private baseUrl = '/subscription';
  private subscriptionsUrl = '/subscriptions';
  private tokensUrl = '/tokens';
  private paymentsUrl = '/payments';

  // ============================================
  // 📋 CATALOGUE DES PLANS
  // ============================================

  /**
   * Récupère la liste des plans disponibles
   * GET /pricing/plans
   */
  async getPlans(params?: GetPlansParams): Promise<SubscriptionPlan[]> {
    try {
      // Utiliser l'endpoint recommandé /pricing/plans
      const response = await api.get<ApiResponse<SubscriptionPlan[]>>('/pricing/plans', params);
      return response.data;
    } catch (error) {
      console.warn('⚠️ API /pricing/plans indisponible, fallback vers service local:', error);
      
      // Fallback vers modernSubscriptionService qui contient les mocks
      const { default: modernSubscriptionService } = await import('./modernSubscription');
      return modernSubscriptionService.getSubscriptionPlans(params);
    }
  }

  /**
   * Récupère les plans disponibles pour l'utilisateur connecté (selon son type de compte)
   * GET /subscriptions/plans/available
   */
  async getAvailablePlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get<ApiResponse<SubscriptionPlan[]>>(`${this.subscriptionsUrl}/plans/available`);
      return response.data;
    } catch (error) {
      console.warn('⚠️ API /subscriptions/plans/available indisponible, fallback vers getPlans:', error);
      return this.getPlans();
    }
  }

  // ============================================
  // 💳 ACHAT D'ABONNEMENT - MOBILE MONEY
  // ============================================

  /**
   * 🆕 Achète un plan via Mobile Money
   * POST /subscriptions/purchase
   * 
   * C'est l'endpoint principal pour les achats Mobile Money.
   * Retourne un transactionId pour le polling du statut.
   */
  async purchasePlan(data: PurchaseRequest): Promise<PurchaseResponse> {
    // Résolution de la quantité pour les plans `per_unit` (coopératives) :
    //  1. data.quantity (caller explicite)
    //  2. sessionStorage.cooperative_member_count (slider Pricing.tsx)
    //  3. omis → backend défaut 1 (transparent pour les plans flat PME)
    let resolvedQuantity = data.quantity;
    if (resolvedQuantity == null && typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem('cooperative_member_count');
        const parsed = stored != null ? parseInt(stored, 10) : NaN;
        if (Number.isFinite(parsed) && parsed >= 1) {
          resolvedQuantity = parsed;
        }
      } catch {
        // sessionStorage indisponible — ignore
      }
    }

    console.log('📱 [Mobile Money] Initiation achat:', {
      planId: data.planId,
      telecom: data.telecom,
      billingPeriod: data.billingPeriod || 'monthly',
      promoCode: data.promoCode || null,
      quantity: resolvedQuantity ?? '(default)',
      phone: data.clientPhone.substring(0, 6) + '****'
    });

    const response = await api.post<PurchaseResponse>(
      `${this.subscriptionsUrl}/purchase`,
      {
        planId: data.planId,
        clientPhone: data.clientPhone,
        telecom: data.telecom,
        channel: data.channel || 'merchant',
        clientReference: data.clientReference,
        billingPeriod: data.billingPeriod || 'monthly',  // 🆕 Période
        promoCode: data.promoCode || undefined,          // 🆕 Code promo
        // Inclus uniquement si défini — le backend l'ignore pour les plans flat
        ...(resolvedQuantity != null ? { quantity: resolvedQuantity } : {}),
      }
    );

    return response;
  }

  /**
   * 🆕 Prévisualise le prix d'un abonnement avant achat
   * GET /subscriptions/price-preview
   * 
   * Utilise le PromoCodeService du backend pour valider les codes promo
   */
  async previewSubscriptionPrice(params: {
    planId: string;
    billingPeriod?: BillingPeriod;
    promoCode?: string;
    isUpgrade?: boolean;
  }): Promise<PricePreviewResponse> {
    console.log('💰 [Price Preview] Calcul du prix:', params);

    const queryParams = new URLSearchParams();
    queryParams.append('planId', params.planId);
    if (params.billingPeriod) queryParams.append('billingPeriod', params.billingPeriod);
    if (params.promoCode) queryParams.append('promoCode', params.promoCode);
    if (params.isUpgrade) queryParams.append('isUpgrade', 'true');

    const response = await api.get<PricePreviewResponse>(
      `${this.subscriptionsUrl}/price-preview?${queryParams.toString()}`
    );

    return response;
  }

  /**
   * 🆕 Upgrade vers un nouveau plan
   * POST /subscriptions/upgrade
   * 
   * Gère le prorata automatique et l'application immédiate
   */
  async upgradeSubscription(data: UpgradeSubscriptionRequest): Promise<PurchaseResponse> {
    console.log('⬆️ [Upgrade] Changement de plan:', {
      newPlanId: data.newPlanId,
      telecom: data.telecom,
      billingPeriod: data.billingPeriod || 'monthly',
      applyImmediately: data.applyImmediately ?? true,
      phone: data.clientPhone.substring(0, 6) + '****'
    });

    const response = await api.post<PurchaseResponse>(
      `${this.subscriptionsUrl}/upgrade`,
      {
        newPlanId: data.newPlanId,
        clientPhone: data.clientPhone,
        telecom: data.telecom,
        billingPeriod: data.billingPeriod || 'monthly',
        promoCode: data.promoCode || undefined,
        applyImmediately: data.applyImmediately ?? true
      }
    );

    return response;
  }

  /**
   * Convertit les données du formulaire de paiement vers PurchaseRequest
   */
  createPurchaseRequest(
    planId: string,
    countryCode: string,
    phoneNumber: string,
    provider: string,
    channel: PaymentChannel = 'merchant'
  ): PurchaseRequest {
    return {
      planId,
      clientPhone: formatPhoneNumber(countryCode, phoneNumber),
      telecom: getProviderTelecomCode(provider),
      channel
    };
  }

  // ============================================
  // 💳 GESTION DES ABONNEMENTS (Ancien endpoint)
  // ============================================

  /**
   * @deprecated Utiliser purchasePlan() pour les nouveaux achats Mobile Money
   * Crée un abonnement (ancien format)
   */
  async createSubscription(data: {
    planId: string;
    paymentMethod: {
      type: 'card' | 'mobile';
      // Carte bancaire
      cardNumber?: string;
      expiryMonth?: number;
      expiryYear?: number;
      cvc?: string;
      name?: string;
      // Mobile Money
      mobileMoneyProvider?: string;
      mobileMoneyNumber?: string;
      mobilePin?: string;
    };
    billingDetails?: {
      name: string;
      email: string;
      address: {
        line1: string;
        city: string;
        postalCode: string;
        country: string;
      };
    };
  }): Promise<Subscription> {
    // Mapper les données pour correspondre au format attendu par le backend
    const apiPayload = {
      planId: data.planId,
      paymentMethod: {
        type: data.paymentMethod.type,
        // Carte bancaire
        cardNumber: data.paymentMethod.cardNumber,
        expiryMonth: data.paymentMethod.expiryMonth,
        expiryYear: data.paymentMethod.expiryYear,
        cvc: data.paymentMethod.cvc,
        name: data.paymentMethod.name,
        // Mobile Money - mapper vers les noms attendus par le backend
        provider: data.paymentMethod.mobileMoneyProvider,
        phoneNumber: data.paymentMethod.mobileMoneyNumber,
        pin: data.paymentMethod.mobilePin
      },
      billingDetails: data.billingDetails
    };
    
    const response = await api.post<ApiResponse<Subscription>>(`${this.baseUrl}`, apiPayload);
    return response.data;
  }

  async getCurrentSubscription(): Promise<Subscription> {
    const response = await api.get<ApiResponse<Subscription> | Subscription>(`${this.baseUrl}/current`);
    // Le backend peut renvoyer {data: Subscription} ou directement Subscription
    return unwrapData(response);
  }

  /**
   * 🆕 Récupère le statut effectif de l'abonnement
   * GET /subscription/effective-status
   * 
   * Endpoint RECOMMANDÉ pour vérifier l'accès utilisateur.
   * Inclut la gestion du grace period, downgrades et messages de statut.
   */
  async getEffectiveStatus(): Promise<EffectiveStatusResponse> {
    try {
      const response = await api.get<ApiResponse<EffectiveStatusResponse> | EffectiveStatusResponse>(`${this.baseUrl}/effective-status`);
      return unwrapData(response);
    } catch (error) {
      console.error('❌ Erreur getEffectiveStatus:', error);
      // Fallback: construire une réponse minimale depuis getCurrentSubscription
      try {
        const subscription = await this.getCurrentSubscription();
        return {
          subscription,
          customer: {
            id: subscription.customerId,
            email: '',
            type: CustomerType.SME
          },
          isDowngraded: false,
          gracePeriod: {
            inGracePeriod: false,
            daysRemaining: 0
          },
          planName: subscription.plan?.name || 'Freemium',
          planTier: (subscription.plan?.name?.toLowerCase() || 'freemium') as 'freemium' | 'standard' | 'premium' | 'enterprise',
          statusMessage: subscription.status === 'active' ? 'Abonnement actif' : 'Statut inconnu'
        };
      } catch {
        throw error; // Re-throw si même le fallback échoue
      }
    }
  }

  async cancelSubscription(): Promise<Subscription> {
    const response = await api.post<ApiResponse<Subscription> | Subscription>(`${this.baseUrl}/cancel`, {});
    return unwrapData(response);
  }

  async changeSubscriptionPlan(data: {
    planId: string;
    effectiveDate?: string;
  }): Promise<Subscription> {
    const response = await api.post<ApiResponse<Subscription> | Subscription>(`${this.baseUrl}/change-plan`, data);
    return unwrapData(response);
  }

  // ============================================
  // 📱 MOBILE MONEY - Vérification du statut de paiement
  // ============================================

  /**
   * Vérifie le statut d'un paiement Mobile Money
   * @param transactionId - ID de la transaction retourné lors de la création
   * @returns Statut du paiement avec détails
   */
  async getPaymentStatus(transactionId: string): Promise<PaymentStatusResponse> {
    const response = await api.get<ApiResponse<PaymentStatusResponse>>(
      `${this.subscriptionsUrl}/payment-status/${transactionId}`
    );
    return response.data;
  }

  /**
   * Polling du statut de paiement Mobile Money avec retry automatique
   * @param transactionId - ID de la transaction
   * @param options - Options de polling (intervalle, timeout, callbacks)
   * @returns Promise résolvant avec le résultat final du paiement
   */
  async pollPaymentStatus(
    transactionId: string,
    options: {
      pollInterval?: number;      // Intervalle entre les vérifications (ms)
      maxDuration?: number;       // Durée maximale du polling (ms)
      onStatusChange?: (status: PaymentStatusResponse) => void;
      onError?: (error: Error) => void;
    } = {}
  ): Promise<PaymentStatusResponse> {
    const {
      pollInterval = 5000,       // 5 secondes par défaut
      maxDuration = 300000,      // 5 minutes par défaut
      onStatusChange,
      onError
    } = options;

    const startTime = Date.now();
    let lastStatus: string | null = null;

    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        // Vérifier si le timeout est atteint
        if (Date.now() - startTime >= maxDuration) {
          const timeoutResult: PaymentStatusResponse = {
            transactionId,
            status: 'expired',
            message: 'Le délai de confirmation a expiré. Veuillez réessayer.',
            failureReason: 'TIMEOUT'
          };
          reject(timeoutResult);
          return;
        }

        try {
          const statusResponse = await this.getPaymentStatus(transactionId);

          // Notifier du changement de statut si callback fourni
          if (onStatusChange && statusResponse.status !== lastStatus) {
            lastStatus = statusResponse.status;
            onStatusChange(statusResponse);
          }

          // Vérifier le statut final
          switch (statusResponse.status) {
            case 'completed':
              console.log('✅ [Mobile Money] Paiement confirmé:', transactionId);
              resolve(statusResponse);
              return;

            case 'failed':
              console.error('❌ [Mobile Money] Paiement échoué:', statusResponse.failureReason);
              reject(statusResponse);
              return;

            case 'expired':
              console.warn('⏰ [Mobile Money] Paiement expiré:', transactionId);
              reject(statusResponse);
              return;

            case 'pending':
              // Continuer le polling
              console.log('⏳ [Mobile Money] En attente de confirmation...', {
                transactionId,
                elapsedTime: Math.round((Date.now() - startTime) / 1000) + 's'
              });
              setTimeout(checkStatus, pollInterval);
              break;

            default:
              // Statut inconnu, continuer le polling
              setTimeout(checkStatus, pollInterval);
          }

        } catch (error) {
          console.error('❌ [Mobile Money] Erreur lors de la vérification:', error);
          
          if (onError) {
            onError(error as Error);
          }
          
          // En cas d'erreur réseau, continuer à essayer
          // (le backend peut être temporairement indisponible)
          setTimeout(checkStatus, pollInterval);
        }
      };

      // Démarrer le premier check immédiatement
      checkStatus();
    });
  }

  /**
   * Créer un abonnement avec support Mobile Money (gestion asynchrone)
   * Retourne soit un abonnement actif, soit les infos de paiement en attente
   */
  async createSubscriptionWithMobileMoney(data: {
    planId: string;
    paymentMethod: {
      type: 'mobile';
      mobileMoneyProvider: string;
      mobileMoneyNumber: string;
      mobilePin?: string;
    };
    billingDetails?: {
      name: string;
      email: string;
      address?: {
        line1: string;
        city: string;
        postalCode: string;
        country: string;
      };
    };
  }): Promise<Subscription | MobileMoneyPendingResponse> {
    const apiPayload = {
      planId: data.planId,
      paymentMethod: {
        type: 'mobile',
        provider: data.paymentMethod.mobileMoneyProvider,
        phoneNumber: data.paymentMethod.mobileMoneyNumber,
        pin: data.paymentMethod.mobilePin
      },
      billingDetails: data.billingDetails
    };

    const response = await api.post<ApiResponse<Subscription | MobileMoneyPendingResponse>>(
      `${this.baseUrl}`,
      apiPayload
    );

    return response.data;
  }

  // ============================================
  // 🪙 TOKENS
  // ============================================

  /**
   * Récupère le solde de tokens de l'utilisateur
   * GET /tokens/balance
   */
  async getTokenBalance(): Promise<CustomerTokenBalance> {
    const response = await api.get<ApiResponse<CustomerTokenBalance>>(`${this.tokensUrl}/balance`);
    return response.data;
  }

  /**
   * Récupère l'historique des transactions de tokens
   * GET /tokens/transactions
   */
  async getTokenTransactions(params?: PaginationParams): Promise<{
    transactions: TokenTransaction[];
    meta: { pagination: { page: number; limit: number; total: number; pages: number } };
  }> {
    const response = await api.get<ApiResponse<TokenTransaction[]>>(`${this.tokensUrl}/transactions`, params);
    return {
      transactions: response.data,
      meta: response.meta?.pagination ? { pagination: response.meta.pagination } : { pagination: { page: 1, limit: 10, total: 0, pages: 0 } }
    };
  }

  // ============================================
  // 🪙 TOKENS PREMIUM - Achat de tokens supplémentaires
  // ============================================

  /**
   * Initie un achat de tokens premium via Mobile Money
   * POST /subscriptions/purchase-tokens
   */
  async purchasePremiumTokens(data: {
    tokenAmount: number;
    priceUSD: number;
    clientPhone: string;
    telecom: string;
    channel?: string;
    clientReference?: string;
  }): Promise<PurchaseResponse> {
    const response = await api.post<PurchaseResponse>(`${this.subscriptionsUrl}/purchase-tokens`, {
      tokenAmount: data.tokenAmount,
      priceUSD: data.priceUSD,
      clientPhone: data.clientPhone,
      telecom: data.telecom,
      channel: data.channel || 'merchant',
      clientReference: data.clientReference,
    });
    return response;
  }

  // ============================================
  // 💰 PAIEMENTS ET HISTORIQUE
  // ============================================

  /**
   * 🆕 Récupère l'historique des paiements d'abonnement
   * GET /subscriptions/current/payment-history
   * 
   * Utilisé pour afficher les factures/reçus côté frontend
   */
  async getSubscriptionPaymentHistory(): Promise<PaymentHistoryItem[]> {
    try {
      const response = await api.get<ApiResponse<PaymentHistoryItem[]>>(
        `${this.subscriptionsUrl}/current/payment-history`
      );
      return response.data;
    } catch (error) {
      console.warn('⚠️ Historique paiements abonnement indisponible:', error);
      return [];
    }
  }

  /**
   * Récupère l'historique général des paiements
   * GET /payments
   */
  async getPaymentHistory(params?: PaginationParams): Promise<{
    payments: Payment[];
    meta: { pagination: { page: number; limit: number; total: number; pages: number } };
  }> {
    const response = await api.get<ApiResponse<Payment[]>>(this.paymentsUrl, params);
    
    // Mapper les données backend vers le format UI compatible
    const mappedPayments = response.data.map(payment => ({
      ...payment,
      // Propriétés compatibilité UI
      date: payment.createdAt || payment.completedAt || new Date().toISOString(),
      method: this.mapPaymentMethodToString(payment.paymentMethod),
      status: this.mapPaymentStatusToUI(payment.paymentStatus),
      receiptUrl: `/payments/${payment.id}/receipt`
    }));
    
    return {
      payments: mappedPayments,
      meta: response.meta?.pagination ? { pagination: response.meta.pagination } : { pagination: { page: 1, limit: 10, total: 0, pages: 0 } }
    };
  }
  
  private mapPaymentMethodToString(method: string): string {
    const mapping: Record<string, string> = {
      'card': 'Carte bancaire',
      'mobile': 'Mobile Money',
      'bank_transfer': 'Virement bancaire',
      'manual': 'Paiement manuel'
    };
    return mapping[method] || method;
  }
  
  private mapPaymentStatusToUI(status: string): 'Payé' | 'En attente' | 'Échoué' {
    const mapping: Record<string, 'Payé' | 'En attente' | 'Échoué'> = {
      'completed': 'Payé',
      'success': 'Payé',
      'pending': 'En attente',
      'failed': 'Échoué',
      'refunded': 'Payé',
      'cancelled': 'Échoué'
    };
    return mapping[status] || 'En attente';
  }

  /**
   * Télécharge un reçu de paiement en PDF
   * GET /payments/{paymentId}/receipt
   */
  async downloadReceipt(paymentId: string): Promise<Blob> {
    // Utiliser l'API base URL depuis l'environnement
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
    const token = getToken();
    const response = await fetch(`${baseUrl}${this.paymentsUrl}/${paymentId}/receipt`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/pdf'
      },
    });
    return response.blob();
  }

  /**
   * Upload une preuve de paiement manuel via Cloudinary
   * POST /payments/manual
   * Flux: Fichier → Cloudinary → URL → Backend
   */
  async uploadManualPaymentProof(data: {
    planId?: string;
    tokenAmount?: number;
    proofFile: File;
    referenceNumber: string;
    amount: number;
    paymentDate: string;
  }): Promise<{ message: string; referenceId: string }> {
    // Étape 1: Upload la preuve de paiement sur Cloudinary
    console.log('📤 Upload de la preuve de paiement sur Cloudinary...');
    const cloudinaryResult = await cloudinaryService.uploadDocument(data.proofFile, 'payments');
    const proofUrl = cloudinaryResult.url;
    console.log('✅ Preuve uploadée sur Cloudinary:', proofUrl);
    
    // Étape 2: Envoyer les données avec l'URL au backend
    const requestData: {
      proofUrl: string;
      referenceNumber: string;
      amount: number;
      paymentDate: string;
      planId?: string;
      tokenAmount?: number;
    } = {
      proofUrl,
      referenceNumber: data.referenceNumber,
      amount: data.amount,
      paymentDate: data.paymentDate
    };
    
    if (data.planId) {
      requestData.planId = data.planId;
    }
    if (data.tokenAmount) {
      requestData.tokenAmount = data.tokenAmount;
    }

    const response = await api.post<ApiResponse<{ message: string; referenceId: string }>>(
      `${this.paymentsUrl}/manual`,
      requestData
    );
    return response.data;
  }
}

export const subscriptionApiService = new SubscriptionApiService();
export default subscriptionApiService;